/*******************************************************************************
 * Copyright (c) 2024 Belltane LLC
 *
 * All rights reserved.
 *
 * Contributors:
 *   Amina Rais - Initial implementation
 *
 * Version: 1.0.0
 *
 *******************************************************************************/
import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';

import Container from '@mui/material/Container';
import PageSubHeader from '../Layout/PageSubHeader'


const TOS = () => {


  const { classes } = useStyles();
  const { t } = useTranslation()

  return (
    <React.Fragment>
      <PageSubHeader location={t('titles:tos')} />
      <Grid container spacing={4} className={classes.pageContainer}>
        <Grid item xs={12} sm={12} md={12}>


          <Container maxWidth="md" component="main">
            <Container maxWidth="sm">
              <Typography style={{ color: "#383e56", textTransform: "uppercase" }} component="h1" variant="h2" align="center" color="textPrimary" gutterBottom>
                Terms of Service for Pictitude
              </Typography>

              <div className={classes.sectionTitleBorder}></div>



            </Container>
          </Container>
          <Container className={classes.cardGrid} maxWidth="md">
            <Grid container maxwidth="sm" justify="center" className={classes.pageContainer} spacing={5} alignItems="stretch" >

              <Grid item xs={12} sm={12} md={12}>

                <Typography variant="h5" className={classes.title}>
                  These Terms of Service ("Terms") govern your access to and use of the Pictitude website and mobile application ("Platform"). By accessing or using the Platform, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Platform.
                </Typography>
                <Typography variant="h3" className={classes.title}>1. Eligibility.</Typography>
                <Typography variant="h5" className={classes.title}>
                  You must be at least 13 years old to use the Platform. By using the Platform, you represent and warrant that you are at least 13 years old and have the legal capacity to enter into these Terms.
                </Typography>
                <Typography variant="h3" className={classes.title}>2. Access and Use

                </Typography>
                <Typography variant="h5" className={classes.title}>
                  2.1. Subject to your compliance with these Terms, we grant you a non-exclusive, limited, revocable, non-transferable license to access and use the Platform for your personal, non-commercial use.
                </Typography>
                <Typography variant="h3" className={classes.title}>2.2. You may not use the Platform for any illegal or unauthorized purpose. You agree to comply with all applicable laws and regulations in connection with your use of the Platform.

                </Typography>
                <Typography variant="h5" className={classes.title}>
                  2.3. You may not use the Platform in any way that could damage, disable, overburden, or impair the Platform or interfere with any other person's use and enjoyment of the Platform.

                </Typography>
                <Typography variant="h3" className={classes.title}> 2.4. You may not attempt to gain unauthorized access to the Platform or any related systems or networks.
                </Typography>
                <Typography variant="h5" className={classes.title}>
                  2.5  You may need to create an account to access and use some features of the Service. You are responsible for maintaining the confidentiality of your account information, including your username and password. You are also responsible for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account or any other breach of security. We will not be liable for any loss or damage arising from your failure to comply with this provision.
                </Typography>
                <Typography variant="h3" className={classes.title}> 3. Content
                </Typography>
                <Typography variant="h5" className={classes.title}>
                  The Service allows you to post, share, and upload content, including photos, videos, and text ("User Content"). You retain all ownership rights to your User Content. However, by posting User Content to the Service, you grant us a non-exclusive, royalty-free, transferable, sub-licensable, worldwide license to host, use, distribute, modify, run, copy, publicly perform or display, translate, and create derivative works of your User Content (consistent with your privacy and application settings).
                </Typography>
                <Typography variant="h3" className={classes.title}> 3.1. You are solely responsible for all content that you upload, post, or otherwise transmit through the Platform ("Content").

                </Typography>
                <Typography variant="h5" className={classes.title}>
                  3.2. You represent and warrant that you own or have the necessary rights and permissions to use and distribute all Content that you upload, post, or otherwise transmit through the Platform.

                </Typography>
                <Typography variant="h3" className={classes.title}>3.3. You agree that you will not upload, post, or otherwise transmit any Content that:

                </Typography>
                <Typography variant="h5" className={classes.title}>
                  is illegal, harmful, threatening, abusive, harassing, tortious, defamatory, vulgar, obscene, libelous, invasive of another's privacy, hateful, or racially, ethnically or otherwise objectionable;
                  infringes any patent, trademark, trade secret, copyright, or other intellectual property right of any other person;
                  contains software viruses or any other computer code, files, or programs that are designed or intended to disrupt, damage, or limit the functionality of any computer software or hardware; or
                  impersonates any person or entity, including any of our employees or representatives.

                </Typography>
                <Typography variant="h3" className={classes.title}>

                  3.4. We reserve the right, but are not obligated, to monitor or review any Content. We may remove any Content that, in our sole discretion, violates these Terms or is otherwise objectionable.

                </Typography>
                <Typography variant="h5" className={classes.title}>

                  4. User Conduct

                </Typography>
                <Typography variant="h3" className={classes.title}>
                  4.1. You agree to use the Platform in a respectful manner and to comply with all applicable laws and regulations.

                </Typography>
                <Typography variant="h3" className={classes.title}>
                  4.2. You agree not to engage in any conduct that could be considered harmful, offensive, or illegal. This includes, but is not limited to:

                </Typography>

                <ul>
                  <li>
                    <Typography variant="h5" className={classes.title}>  Spamming or flooding the Platform with unsolicited messages.</Typography>
                  </li>
                  <li>
                    <Typography variant="h5" className={classes.title}> Abusing, harassing, or threatening other users.</Typography>
                  </li>

                  <li>
                    <Typography variant="h5" className={classes.title}>
                      Collecting or storing personal information about other users without their consent.</Typography>
                  </li>
                  <li>
                    <Typography variant="h5" className={classes.title}> Impersonating any person or entity.</Typography>
                  </li>
                  <li>
                    <Typography variant="h5" className={classes.title}> Engaging in commercial activity without our prior written consent.</Typography>
                  </li>
                </ul>
                <Typography variant="h3" className={classes.title}>
                  4.3. We reserve the right, but are not obligated, to monitor or review any user conduct. We may take any action we deem necessary, including terminating your account, if you violate these Terms or engage in any other conduct that we deem harmful, offensive, or illegal.

                </Typography>
                <Typography variant="h3" className={classes.title}>
                  5. Intellectual Property

                </Typography>
                <Typography variant="h3" className={classes.title}>
                  5.1. The Platform is protected by copyright, trademark, and other intellectual property laws. You agree not to copy, modify, distribute, sell, or lease any part of the Platform without our prior written consent.

                </Typography>
                <Typography variant="h3" className={classes.title}>
                  5.2. We do not claim ownership of any Content that you upload, post, or otherwise transmit through the Platform. However, by uploading, posting, or otherwise transmitting Content to the Platform, you grant us a non-exclusive, worldwide, royalty-free, sublicensable, and transferable license to use, reproduce, modify, distribute, publish, and display such Content in connection with the Platform.

                </Typography>
                <Typography variant="h3" className={classes.title}>
                  6. Termination

                </Typography>
                <Typography variant="h3" className={classes.title}>
                  6.1. We may terminate your account at any time for any reason. We may also terminate your access to the Platform without prior notice if you violate any of these Terms.

                </Typography>
                <Typography variant="h3" className={classes.title}>
                  6.2. Upon termination of your account, your access to the Platform will be terminated and you may lose any Content that you have uploaded, posted, or otherwise transmitted through the Platform.

                </Typography>
                <Typography variant="h3" className={classes.title}>
                  7. Disclaimers

                </Typography>
                <Typography variant="h3" className={classes.title}>

                  7.1. THE SERVICE IS PROVIDED ON AN "AS IS" BASIS WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING, BUT NOT LIMITED TO, WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                </Typography>
                <Typography variant="h5" className={classes.title}>
                  WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, VIRUS-FREE, OR SECURE. WE ARE NOT RESPONSIBLE FOR ANY LOSS OF DATA OR DAMAGE TO YOUR COMPUTER OR OTHER DEVICE ARISING FROM YOUR USE OF THE SERVICE.
                </Typography>
                <Typography variant="h3" className={classes.title}>
                  8. Limitation of Liability
                </Typography>
                <Typography variant="h3" className={classes.title}>
                  8.1  We will not be liable for any damages, including but not limited to direct, indirect, incidental, consequential, or punitive damages, arising out of your access to or use of the Service, even if we have been advised of the possibility of such damages.
                </Typography>
                <Typography variant="h3" className={classes.title}>
                  9. Indemnification
                </Typography>
                <Typography variant="h3" className={classes.title}>
                  9.1 You agree to indemnify and hold harmless us, our officers, directors, employees, agents, and licensors from and against any and all claims, demands, losses, liabilities, costs, and expenses (including attorneys' fees) arising out of or related to your use of the Service or your violation of these Terms.
                </Typography>
                <Typography variant="h3" className={classes.title}>
                  10. Governing Law
                </Typography>
                <Typography variant="h3" className={classes.title}>
                  10.1 These Terms shall be governed and construed in accordance with the laws of the State of Georgia, USA, without regard to its conflict of laws provisions. Any legal action or proceeding arising out of or relating to these Terms shall be brought exclusively in the courts of the State of Georgia, USA.
                </Typography>
                <Typography variant="h3" className={classes.title}>
                  11. Entire Agreement
                </Typography>
                <Typography variant="h3" className={classes.title}>
                  11.1 These Terms constitute the entire agreement between you and us with respect to your access and use of the Service.
                </Typography>
                <Typography variant="h3" className={classes.title}>
                  12. Changes to the Terms
                </Typography>
                <Typography variant="h3" className={classes.title}>
                  12.1 We reserve the right to modify these Terms at any time. We will notify you of any changes by posting the new Terms on the Service. Your continued use of the Service after such posting constitutes your acceptance of the new Terms.
                </Typography>
              </Grid>




            </Grid>

          </Container>
        </Grid>
      </Grid>




    </React.Fragment >
  );
}
const useStyles = makeStyles({ "name": { TOS } })((theme) => ({

  pageContainer: {
    backgroundColor: '#dce7eb',
    padding: '1.5%',

  },

  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },



  sectionTitleBorder: {
    width: '100px',
    height: '2px',
    marginRight: 'auto',
    marginLeft: 'auto',
    backgroundColor: '#fb743e',
    marginBottom: '30px',

  },


  title: {
    paddingTop: '20px',
    color:"#383e56"
  }
}));
export default TOS;
